/**
 * `/presenter` — presenter onboarding (T7): pick a fixture from `GET /api/fixtures`, choose (or randomize) a
 * seed, optionally supply the presenter key, `POST /api/presenter/sessions`, then hand off to
 * `/presenter/:sessionId` (`PresenterPage.tsx`). The presenter key is only ever sent in the request body —
 * never placed in a URL or query string — and credentials are stored the same way every other entry point
 * stores them (`saveCredentials`), just with `role: 'presenter'`.
 */
import { useEffect, useState, type FormEvent } from 'react';
import type { FixturesResponse } from '@momentum/shared';
import { ApiError, createPresenterSession, getFixtures } from '../api.ts';
import { saveCredentials } from '../session/credentials.ts';
import { useNavigate } from '../shell/router.tsx';
import { strings } from '../shell/strings.ts';

type Fixture = FixturesResponse['fixtures'][number];

/** Positive 31-bit integer: fits `z.number().int()` and every RNG seed the server accepts. */
function randomSeed(): number {
  return Math.floor(Math.random() * 0x7fffffff);
}

export function PresenterEntry() {
  const navigate = useNavigate();
  const [fixtures, setFixtures] = useState<Fixture[] | null>(null);
  const [fixtureId, setFixtureId] = useState('');
  const [seed, setSeed] = useState(randomSeed);
  const [presenterName, setPresenterName] = useState('Presenter');
  const [presenterKey, setPresenterKey] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getFixtures()
      .then((res) => {
        if (cancelled) return;
        setFixtures(res.fixtures);
        setFixtureId((prev) => prev || res.fixtures[0]?.id || '');
      })
      .catch(() => {
        if (!cancelled) setFixtures([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!fixtureId) return;
    setSubmitting(true);
    setError(null);
    try {
      const response = await createPresenterSession({
        fixtureId,
        seed,
        presenterName: presenterName.trim() || undefined,
        presenterKey: presenterKey.trim() ? presenterKey.trim() : undefined,
      });
      // The presenter's own participant ID never appears in `PresenterCreateResponse` (only the simulated
      // roster's IDs do) — it arrives in the WebSocket `welcome.self`. `credentials.participantId` is not
      // read anywhere on the presenter path, so a placeholder is harmless here.
      saveCredentials(response.sessionId, {
        participantId: 'presenter',
        token: response.presenterToken,
        role: 'presenter',
        isHost: true,
      });
      navigate(`/presenter/${response.sessionId}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : strings.common.error);
      setSubmitting(false);
    }
  }

  return (
    <div className="page stack">
      <h1>{strings.presenterEntry.title}</h1>
      <p className="text-muted">{strings.presenterEntry.description}</p>

      {fixtures === null ? (
        <p role="status" aria-live="polite">
          {strings.presenterEntry.fixtureLoading}
        </p>
      ) : fixtures.length === 0 ? (
        <p className="badge badge-danger">{strings.presenterEntry.fixtureNone}</p>
      ) : (
        <form className="stack" onSubmit={submit}>
          <fieldset className="stack-sm" style={{ border: 'none', padding: 0, margin: 0 }}>
            <legend>{strings.presenterEntry.fixtureLabel}</legend>
            {fixtures.map((f) => (
              <label key={f.id} className="choice-row">
                <input type="radio" name="fixture" checked={fixtureId === f.id} onChange={() => setFixtureId(f.id)} />
                <span>
                  <strong>{f.name}</strong> — {f.description}
                </span>
              </label>
            ))}
          </fieldset>

          <div className="field">
            <label htmlFor="presenter-seed">{strings.presenterEntry.seedLabel}</label>
            <div className="row">
              <input
                id="presenter-seed"
                type="number"
                step={1}
                value={seed}
                onChange={(e) => setSeed(Math.trunc(Number(e.target.value)) || 0)}
              />
              <button type="button" className="button" onClick={() => setSeed(randomSeed())}>
                {strings.presenterEntry.seedRandomize}
              </button>
            </div>
            <p className="text-muted">{strings.presenterEntry.seedHint}</p>
          </div>

          <div className="field">
            <label htmlFor="presenter-name">{strings.presenterEntry.presenterNameLabel}</label>
            <input
              id="presenter-name"
              type="text"
              maxLength={32}
              value={presenterName}
              onChange={(e) => setPresenterName(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="presenter-key">{strings.presenterEntry.presenterKeyLabel}</label>
            <input
              id="presenter-key"
              type="password"
              autoComplete="off"
              value={presenterKey}
              onChange={(e) => setPresenterKey(e.target.value)}
            />
          </div>

          {error ? (
            <p className="badge badge-danger" role="alert">
              {error}
            </p>
          ) : null}

          <button type="submit" className="button button-primary" disabled={submitting || !fixtureId}>
            {submitting ? strings.presenterEntry.submitting : strings.presenterEntry.submit}
          </button>
        </form>
      )}
      <footer className="footer">{strings.footer.conceptPrototype}</footer>
    </div>
  );
}
